import type { PtzLimits, PtzState } from "../api/ptz.ts";

const SPAN360_EPS = 1e-3;

/** Mirrors server `wrapDeg` in `ptz.service.ts` for pan unwrap math. */
function wrapDegLikeServer(deg: number, lo: number, hi: number): number {
  const span = hi - lo;
  if (span <= 0) return Math.min(hi, Math.max(lo, deg));
  if (Math.abs(span - 360) < SPAN360_EPS) {
    const r = (((deg - lo) % 360) + 360) % 360;
    return r + lo;
  }
  return Math.min(hi, Math.max(lo, deg));
}

export type PtzRingNorm = {
  normX: number;
  normY: number;
  panRawUnwrapped: number;
};

/**
 * Horizontal FOV at optical 1× — ring span on the flat strip ≈ HFOV / 360°.
 * Tune if the 1× marker does not match the PTZ tile width.
 */
/** Compact ring on 360° view (~12° span on the equirect strip at 1×). */
const RING_HFOV_DEG_AT_1X = 15;

/** Reference stream width for “view width in px” at optical 1×. */
export const PTZ_ZOOM_REF_WIDTH_PX = 1920;

/**
 * Approximate horizontal scene span visible at the given optical zoom (px).
 * Scales as 1/zoom from {@link PTZ_ZOOM_REF_WIDTH_PX} at 1×.
 */
export function ptzZoomViewWidthPx(
  zoom: number,
  referenceWidthPx = PTZ_ZOOM_REF_WIDTH_PX,
): number {
  const z = Math.max(1, Number(zoom) || 1);
  return Math.round(referenceWidthPx / z);
}

/** Extra scale on top of the calibrated 1× size (1 = no boost). */
const RING_SIZE_BOOST_OVER_1X = 1.2;

const RING_FRAC_MIN = 0.024;
const RING_FRAC_MAX = 0.34;

/**
 * Ring diameter as a fraction of the flat 360° strip width (0..1).
 * Calibrated at optical 1×, boosted for visibility, and held constant at every zoom level.
 */
export function focusRingDiameterFrac(
  _zoom: number,
  zoomMin: number,
  _zoomMax: number,
): number {
  void _zoom;
  void zoomMin;
  void _zoomMax;
  const fracAt1x = RING_HFOV_DEG_AT_1X / 360;
  const raw = fracAt1x * RING_SIZE_BOOST_OVER_1X;
  return Math.min(RING_FRAC_MAX, Math.max(RING_FRAC_MIN, raw));
}

/**
 * Inverse of server `applyFocus` (panorama norm → pan/tilt). Used when the
 * main view is PTZ so the small 360° PiP ring tracks live pan/tilt/zoom.
 */
export function ptzStateToRingNorm(
  s: PtzState,
  lim: PtzLimits,
  panUnwrapHintDeg: number | null,
): PtzRingNorm {
  const panMin = lim.pan.min;
  const panMax = lim.pan.max;
  const panSpan = panMax - panMin;
  const tiltMin = lim.tilt.min;
  const tiltMax = lim.tilt.max;
  const tiltSpan = tiltMax - tiltMin;
  const panOff = lim.panOffsetDeg;
  const tiltOff = lim.tiltOffsetDeg;

  const normY =
    tiltSpan > 0
      ? Math.min(1, Math.max(0, (s.tilt - tiltMin - tiltOff) / tiltSpan))
      : 0.5;

  let panRaw: number;
  if (panSpan <= 0) {
    panRaw = s.pan;
  } else if (Math.abs(panSpan - 360) < SPAN360_EPS) {
    const wrapped = wrapDegLikeServer(s.pan, panMin, panMax);
    const hint = panUnwrapHintDeg ?? wrapped;
    let best = wrapped;
    let bestDist = Infinity;
    for (let k = -4; k <= 4; k++) {
      const c = wrapped + 360 * k;
      const d = Math.abs(c - hint);
      if (d < bestDist) {
        bestDist = d;
        best = c;
      }
    }
    panRaw = best;
  } else {
    panRaw = Math.min(panMax, Math.max(panMin, s.pan));
  }

  const rawNormX = 0.5 + (panRaw - panOff) / panSpan;
  // Full horizontal wheel: equirectangular left/right edges are the same seam.
  // Clamp would pin the ring at x=0 or x=1 when panRaw crosses ±180°; wrap so
  // the marker continues on the opposite side of the flat strip.
  const normX =
    panSpan > 0 && Math.abs(panSpan - 360) < SPAN360_EPS
      ? ((rawNormX % 1) + 1) % 1
      : Math.min(1, Math.max(0, rawNormX));

  return { normX, normY, panRawUnwrapped: panRaw };
}

/**
 * Forward map: flat panorama norm (0..1) → PTZ pan/tilt/zoom.
 * Matches server `applyFocus` / inverse of {@link ptzStateToRingNorm}.
 */
export function ringNormToPtz(
  normX: number,
  normY: number,
  zoom: number,
  lim: PtzLimits,
): Pick<PtzState, "pan" | "tilt" | "zoom"> {
  const nx = Math.min(1, Math.max(0, normX));
  const ny = Math.min(1, Math.max(0, normY));
  const panSpan = lim.pan.max - lim.pan.min;
  const tiltSpan = lim.tilt.max - lim.tilt.min;
  let pan = (nx - 0.5) * panSpan + lim.panOffsetDeg;
  if (panSpan > 0 && Math.abs(panSpan - 360) < SPAN360_EPS) {
    pan = wrapDegLikeServer(pan, lim.pan.min, lim.pan.max);
  } else if (panSpan > 0) {
    pan = Math.min(lim.pan.max, Math.max(lim.pan.min, pan));
  }
  const tilt =
    tiltSpan > 0
      ? Math.min(
          lim.tilt.max,
          Math.max(
            lim.tilt.min,
            lim.tilt.min + ny * tiltSpan + lim.tiltOffsetDeg,
          ),
        )
      : lim.tilt.min;
  const z = Math.min(lim.zoom.max, Math.max(lim.zoom.min, zoom));
  return { pan, tilt, zoom: z };
}

const clamp01 = (v: number) => Math.min(1, Math.max(0, v));

/**
 * Some deployments mistakenly map tilt as `clamp((1 - normY) * panSpan, tiltMin, tiltMax)`
 * instead of `tiltMin + normY * tiltSpan`. Detect that pattern from a focus response.
 */
export function isBuggyPanSpanTiltFocus(
  sentNormY: number,
  responseTilt: number,
  lim: PtzLimits,
): boolean {
  const panSpan = lim.pan.max - lim.pan.min;
  if (panSpan <= 0) return false;
  const expected = ringNormToPtz(0.5, sentNormY, 1, lim).tilt;
  const buggyTilt = Math.min(
    lim.tilt.max,
    Math.max(lim.tilt.min, (1 - sentNormY) * panSpan),
  );
  if (Math.abs(responseTilt - expected) < 8) return false;
  if (Math.abs(responseTilt - buggyTilt) > 8) return false;
  return (
    responseTilt >= lim.tilt.max - 0.5 && sentNormY > 0.2 && sentNormY < 0.8
  );
}

/** normY to send when the server uses the buggy pan-span tilt formula above. */
export function normYForBuggyPanSpanTiltFocus(
  targetTilt: number,
  lim: PtzLimits,
): number {
  const panSpan = lim.pan.max - lim.pan.min;
  if (panSpan <= 0) return 0.5;
  const clamped = Math.min(lim.tilt.max, Math.max(lim.tilt.min, targetTilt));
  return clamp01(1 - clamped / panSpan);
}

/** Human-readable expected PTZ for a ring position (debug / calibration). */
export function describeRingAim(
  normX: number,
  normY: number,
  zoom: number,
  lim: PtzLimits,
): { pan: number; tilt: number; zoom: number } {
  return ringNormToPtz(normX, normY, zoom, lim);
}

/** Typical 360° equirectangular frame aspect (width / height). */
export const EQUIRECT_VIDEO_ASPECT = 2;

/** Equirectangular row (0 top → 1 bottom) for the horizon / equator guide line. */
export const PANORAMA_HORIZON_NORM_Y = 0.5;

/**
 * Extra normY gain for clicks below {@link PANORAMA_HORIZON_NORM_Y} so PTZ tilt
 * matches the visual target under the dashed horizon guide.
 */
export const NORMY_BELOW_HORIZON_AIM_SCALE = 1.5;

/**
 * Boost normY when the user aims below the horizon dash (raw equirect normY).
 * At/above the line: unchanged. Below: distance from horizon is scaled up.
 */
export function adjustNormYForPtzAim(normY: number): number {
  const ny = clamp01(normY);
  const h = PANORAMA_HORIZON_NORM_Y;
  if (ny <= h) return ny;
  return clamp01(h + (ny - h) * NORMY_BELOW_HORIZON_AIM_SCALE);
}

export type EquirectHorizonLineRect = {
  left: number;
  top: number;
  width: number;
  visible: boolean;
};

/** Full-width dashed horizon on a 2:1 equirect video (`object-fit: cover` or `contain`). */
export function equirectHorizonLineRect(
  width: number,
  height: number,
  normY = PANORAMA_HORIZON_NORM_Y,
  videoAspect = EQUIRECT_VIDEO_ASPECT,
  fit: "cover" | "contain" = "cover",
): EquirectHorizonLineRect {
  if (width <= 0 || height <= 0) {
    return { left: 0, top: 0, width: 0, visible: false };
  }
  const { displayW, displayH, offsetX, offsetY } = equirectVideoRect(
    width,
    height,
    videoAspect,
    fit,
  );
  const ny = clamp01(normY);
  return {
    left: offsetX,
    top: offsetY + ny * displayH,
    width: displayW,
    visible: true,
  };
}

export type EquirectNorm = { normX: number; normY: number };

export type EquirectDisplayPoint = {
  leftPct: number;
  topPct: number;
  /** Ring diameter as % of container width. */
  diameterWidthPct: number;
  visible: boolean;
};

type EquirectFit = "cover" | "contain";

function equirectVideoRect(
  width: number,
  height: number,
  videoAspect: number,
  fit: EquirectFit,
): { displayW: number; displayH: number; offsetX: number; offsetY: number } {
  const containerAspect = width / height;
  if (fit === "contain") {
    if (containerAspect > videoAspect) {
      const displayW = width;
      const displayH = width / videoAspect;
      return {
        displayW,
        displayH,
        offsetX: 0,
        offsetY: (height - displayH) / 2,
      };
    }
    const displayH = height;
    const displayW = height * videoAspect;
    return {
      displayW,
      displayH,
      offsetX: (width - displayW) / 2,
      offsetY: 0,
    };
  }
  if (containerAspect > videoAspect) {
    const displayH = height;
    const displayW = height * videoAspect;
    return {
      displayW,
      displayH,
      offsetX: (width - displayW) / 2,
      offsetY: 0,
    };
  }
  const displayW = width;
  const displayH = width / videoAspect;
  return {
    displayW,
    displayH,
    offsetX: 0,
    offsetY: (height - displayH) / 2,
  };
}

/**
 * Map a click on a 2:1 equirect video (`object-fit: cover` or `contain`) to norm (0..1).
 */
export function clickToEquirectNorm(
  localX: number,
  localY: number,
  width: number,
  height: number,
  videoAspect = EQUIRECT_VIDEO_ASPECT,
  fit: EquirectFit = "cover",
): EquirectNorm | null {
  if (width <= 0 || height <= 0) return null;
  const { displayW, displayH, offsetX, offsetY } = equirectVideoRect(
    width,
    height,
    videoAspect,
    fit,
  );
  const x = localX - offsetX;
  const y = localY - offsetY;
  if (x < 0 || x > displayW || y < 0 || y > displayH) return null;
  return {
    normX: Math.min(1, Math.max(0, x / displayW)),
    normY: Math.min(1, Math.max(0, y / displayH)),
  };
}

/** Map panorama norm to overlay position for a 2:1 equirect video. */
export function equirectNormToDisplay(
  normX: number,
  normY: number,
  width: number,
  height: number,
  diameterFrac: number,
  videoAspect = EQUIRECT_VIDEO_ASPECT,
  fit: EquirectFit = "cover",
): EquirectDisplayPoint {
  if (width <= 0 || height <= 0) {
    return { leftPct: 50, topPct: 50, diameterWidthPct: 8, visible: false };
  }
  const { displayW, displayH, offsetX, offsetY } = equirectVideoRect(
    width,
    height,
    videoAspect,
    fit,
  );
  const x = offsetX + normX * displayW;
  const y = offsetY + normY * displayH;
  return {
    leftPct: (x / width) * 100,
    topPct: (y / height) * 100,
    diameterWidthPct: Math.max(1.5, (diameterFrac * displayW) / width) * 100,
    visible: true,
  };
}
